import React, { useState, useEffect } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Home, Search, Phone, Mail, MapPin, Facebook, Instagram, Twitter, Linkedin, Building2, UserCircle, Eye, Menu, X, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from '../hooks/use-toast';
import { getSettings, createContactMessage } from '../services/api';
import HeaderAuth from './HeaderAuth';
import LiveChatWidget from './LiveChatWidget';
import OwnerBanner from './OwnerBanner';
import { getRecentlyViewed, getTimeAgo } from '../utils/recentlyViewed';

const Layout = ({ children }) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [showRecentlyViewed, setShowRecentlyViewed] = useState(false);
  const [recentVenues, setRecentVenues] = useState([]);
  const [adsenseCode, setAdsenseCode] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showMenuBadge, setShowMenuBadge] = useState(true);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadSettings();
    loadRecentVenues();
    
    // Check if user has already clicked the mobile menu
    const menuBadgeSeen = localStorage.getItem('mobileMenuBadgeSeen');
    if (menuBadgeSeen === 'true') {
      setShowMenuBadge(false);
    }
  }, []);

  // URL parametresi ile iletişim formunu aç (örn: ?contact=mekan-silme&venue=MekanAdi)
  useEffect(() => {
    const contactParam = searchParams.get('contact');
    const venueParam = searchParams.get('venue');
    
    if (contactParam === 'mekan-silme') {
      setShowContactDialog(true);
      setContactForm(prev => ({
        ...prev,
        subject: 'Mekan Silme Talebi',
        message: venueParam ? `Mekan Adı: ${venueParam}\n\nSilme talebimin sebebi:\n` : ''
      }));
      // URL parametresini temizle
      searchParams.delete('contact');
      searchParams.delete('venue');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadRecentVenues = () => {
    const venues = getRecentlyViewed();
    setRecentVenues(venues.slice(0, 5)); // Show max 5 in dropdown
  };

  // Track page visits
  useEffect(() => {
    const trackVisit = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
        await fetch(`${BACKEND_URL}/api/track-visit?path=${encodeURIComponent(location.pathname)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });
      } catch (error) {
        // Silently fail - don't affect user experience
        console.debug('Visitor tracking failed:', error);
      }
    };
    
    trackVisit();
    
    // Reload recent venues on route change (in case user visited a new venue)
    loadRecentVenues();
  }, [location.pathname]); // Track on every route change

  const loadSettings = async () => {
    try {
      const res = await getSettings();
      setSettings(res.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  // Load AdSense code from /api/adsense-settings (separate endpoint)
  useEffect(() => {
    const loadAdsense = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${BACKEND_URL}/api/adsense-settings`);
        const data = await response.json();
        if (data.success && data.data?.adsense_enabled && data.data?.adsense_code) {
          setAdsenseCode(data.data.adsense_code);
        }
      } catch (error) {
        console.error('Error loading AdSense settings:', error);
      }
    };
    loadAdsense();
  }, []);

  // Suppress non-fatal AdSense TagErrors that happen when adsbygoogle.push()
  // races with multiple ad slots. The error doesn't break ads — but React's
  // dev-mode error overlay surfaces it. We swallow ONLY AdSense-origin errors.
  useEffect(() => {
    const handleError = (event) => {
      const msg = event.message || (event.error && event.error.message) || '';
      const src = event.filename || '';
      if (
        src.includes('adsbygoogle.js') ||
        src.includes('googlesyndication') ||
        msg.includes('adsbygoogle.push') ||
        msg.includes("'ins' elements in the DOM")
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return true;
      }
    };
    const handleRejection = (event) => {
      const reason = event.reason;
      const msg = (reason && (reason.message || String(reason))) || '';
      if (msg.includes('adsbygoogle') || msg.includes("'ins' elements")) {
        event.preventDefault();
      }
    };
    window.addEventListener('error', handleError, true);
    window.addEventListener('unhandledrejection', handleRejection, true);
    return () => {
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', handleRejection, true);
    };
  }, []);
  
  // Inject AdSense code into head — DEFERRED to avoid blocking initial page load
  useEffect(() => {
    if (!adsenseCode || !adsenseCode.trim()) return;

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = adsenseCode.trim();
    const loaderScript = tempDiv.querySelector('script[src*="adsbygoogle"]');
    const metas = tempDiv.querySelectorAll('meta');

    const inject = () => {
      // Inject loader script (only once) — skip inline push({}) scripts since
      // page components (AdSenseAd.jsx) manage their own push() calls.
      if (loaderScript && !document.querySelector('script[data-adsense="true"][src*="adsbygoogle"]')) {
        const newScript = document.createElement('script');
        newScript.src = loaderScript.src;
        newScript.async = true;
        newScript.crossOrigin = 'anonymous';
        newScript.setAttribute('data-adsense', 'true');
        document.head.appendChild(newScript);
      }

      // Inject meta tags into head (e.g., google-adsense-account verification)
      metas.forEach(meta => {
        const metaName = meta.getAttribute('name');
        if (metaName && document.querySelector(`meta[name="${metaName}"]`)) return; // already present
        const newMeta = document.createElement('meta');
        Array.from(meta.attributes).forEach(attr => {
          newMeta.setAttribute(attr.name, attr.value);
        });
        document.head.appendChild(newMeta);
      });
    };

    // Defer loading until browser is idle, falling back to window.load — keeps
    // initial paint, hydration, and TTFB as fast as possible.
    let scheduled;
    const schedule = () => {
      if ('requestIdleCallback' in window) {
        scheduled = window.requestIdleCallback(inject, { timeout: 3000 });
      } else {
        scheduled = setTimeout(inject, 1500);
      }
    };

    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }

    return () => {
      if (scheduled && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(scheduled);
      } else if (scheduled) {
        clearTimeout(scheduled);
      }
    };
  }, [adsenseCode]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Backend'e istek gönder
      await createContactMessage(contactForm);
      
      toast({
        title: 'Mesajınız alındı!',
        description: 'En kısa sürede size dönüş yapacağız.',
      });

      // Form'u temizle ve dialog'u kapat
      setContactForm({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      setShowContactDialog(false);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: 'Hata!',
        description: 'Mesajınız gönderilemedi. Lütfen tekrar deneyin.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleContactInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="group flex items-center">
              <img 
                src="/assets/logo/logo-horizontal.svg" 
                alt="DavetEviBul Logo" 
                className="h-10 w-auto transition-transform group-hover:scale-105"
              />
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <Link 
                to="/"
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                  isActive('/') 
                    ? 'text-pink-600 bg-pink-50' 
                    : 'text-gray-600 hover:text-pink-600 hover:bg-gray-50'
                }`}
              >
                <Home className="w-4 h-4" />
                Ana Sayfa
              </Link>
              <Link 
                to="/davet-evleri"
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                  isActive('/davet-evleri') || isActive('/venues') || location.pathname.startsWith('/venue/')
                    ? 'text-pink-600 bg-pink-50' 
                    : 'text-gray-600 hover:text-pink-600 hover:bg-gray-50'
                }`}
              >
                <Search className="w-4 h-4" />
                Mekanları Keşfet
              </Link>
              <button
                onClick={() => setShowContactDialog(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-gray-600 hover:text-pink-600 hover:bg-gray-50"
              >
                <Phone className="w-4 h-4" />
                İletişim
              </button>
            </nav>

            {/* CTA Buttons */}
            <div className="flex items-center gap-3">
              {/* Recently Viewed Dropdown - Desktop Only */}
              {recentVenues.length > 0 && (
                <div className="relative hidden md:block">
                  <button
                    onClick={() => setShowRecentlyViewed(!showRecentlyViewed)}
                    className="relative p-2 text-gray-600 hover:text-pink-600 hover:bg-gray-50 rounded-lg transition-colors"
                    title="Son Baktığınız Davet Evleri"
                  >
                    <Eye className="w-5 h-5" />
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-pink-600 text-white text-xs rounded-full flex items-center justify-center">
                      {recentVenues.length}
                    </span>
                  </button>

                  {/* Dropdown */}
                  {showRecentlyViewed && (
                    <>
                      {/* Backdrop */}
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowRecentlyViewed(false)}
                      />
                      
                      {/* Dropdown Content */}
                      <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[400px] overflow-y-auto">
                        <div className="p-3 border-b border-gray-200">
                          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                            <Eye className="w-4 h-4" />
                            Son Baktığınız Davet Evleri
                          </h3>
                        </div>
                        
                        <div className="divide-y divide-gray-100">
                          {recentVenues.map((venue) => {
                            const citySlug = venue.cityName?.toLowerCase()
                              .replace(/ı/g, 'i')
                              .replace(/ğ/g, 'g')
                              .replace(/ü/g, 'u')
                              .replace(/ş/g, 's')
                              .replace(/ö/g, 'o')
                              .replace(/ç/g, 'c')
                              .replace(/İ/g, 'i')
                              .replace(/[^a-z0-9]+/g, '-')
                              .replace(/^-+|-+$/g, '');
                            
                            const districtSlug = venue.districtName?.toLowerCase()
                              .replace(/ı/g, 'i')
                              .replace(/ğ/g, 'g')
                              .replace(/ü/g, 'u')
                              .replace(/ş/g, 's')
                              .replace(/ö/g, 'o')
                              .replace(/ç/g, 'c')
                              .replace(/İ/g, 'i')
                              .replace(/[^a-z0-9]+/g, '-')
                              .replace(/^-+|-+$/g, '');

                            const venueUrl = `/${citySlug}/${districtSlug}/${venue.slug}`;

                            return (
                              <Link
                                key={venue._id}
                                to={venueUrl}
                                onClick={() => setShowRecentlyViewed(false)}
                                className="flex gap-3 p-3 hover:bg-gray-50 transition-colors"
                              >
                                {/* Thumbnail */}
                                <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-200">
                                  {venue.images ? (
                                    <img
                                      src={venue.images}
                                      alt={venue.name}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <MapPin className="w-6 h-6 text-gray-400" />
                                    </div>
                                  )}
                                </div>

                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-semibold text-sm text-gray-900 line-clamp-1">
                                    {venue.name}
                                  </h4>
                                  <p className="text-xs text-gray-600 mt-1">
                                    {venue.districtName}, {venue.cityName}
                                  </p>
                                  {venue.viewedAt && (
                                    <p className="text-xs text-gray-400 mt-1">
                                      {getTimeAgo(venue.viewedAt)}
                                    </p>
                                  )}
                                </div>
                              </Link>
                            );
                          })}
                        </div>

                        <div className="p-3 border-t border-gray-200 bg-gray-50">
                          <Link
                            to="/"
                            onClick={() => setShowRecentlyViewed(false)}
                            className="text-sm text-pink-600 hover:text-pink-700 font-medium block text-center"
                          >
                            Tümünü Gör →
                          </Link>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              <Link to="/giris">
                <Button 
                  variant="outline"
                  className="flex items-center gap-1 sm:gap-2 border-pink-600 text-pink-600 hover:bg-pink-50 text-xs sm:text-sm px-2 sm:px-4"
                >
                  <Building2 className="w-4 h-4" />
                  <span className="hidden sm:inline">İşletme Girişi</span>
                  <span className="sm:hidden">İşletme</span>
                </Button>
              </Link>

              <HeaderAuth />
              
              {/* Mobile Menu Button */}
              <button
                onClick={() => {
                  setMobileMenuOpen(!mobileMenuOpen);
                  // Hide badge permanently after first click
                  if (showMenuBadge && !mobileMenuOpen) {
                    setShowMenuBadge(false);
                    localStorage.setItem('mobileMenuBadgeSeen', 'true');
                  }
                }}
                className="md:hidden p-2 text-gray-600 hover:text-pink-600 hover:bg-gray-50 rounded-lg transition-colors relative"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                {/* Notification Badge */}
                {!mobileMenuOpen && showMenuBadge && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                    1
                  </span>
                )}
              </button>
            </div>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 py-4">
              <nav className="flex flex-col space-y-2">
                <Link 
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive('/') 
                      ? 'text-pink-600 bg-pink-50 font-medium' 
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Home className="w-5 h-5" />
                  Ana Sayfa
                </Link>
                
                <Link 
                  to="/davet-evleri"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive('/davet-evleri') || location.pathname.startsWith('/venue/')
                      ? 'text-pink-600 bg-pink-50 font-medium' 
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Search className="w-5 h-5" />
                  Mekanları Keşfet
                </Link>
                
                <button
                  onClick={() => {
                    setShowContactDialog(true);
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-gray-600 hover:bg-gray-50"
                >
                  <Phone className="w-5 h-5" />
                  İletişim
                </button>
                
                {/* Recently Viewed - Mobile */}
                {recentVenues.length > 0 && (
                  <div className="px-4 py-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Son Baktıklarınız</p>
                    <div className="space-y-2">
                      {recentVenues.slice(0, 3).map((venue) => {
                        const citySlug = venue.cityName?.toLowerCase()
                          .replace(/ı/g, 'i')
                          .replace(/ğ/g, 'g')
                          .replace(/ü/g, 'u')
                          .replace(/ş/g, 's')
                          .replace(/ö/g, 'o')
                          .replace(/ç/g, 'c')
                          .replace(/İ/g, 'i')
                          .replace(/[^a-z0-9]+/g, '-')
                          .replace(/^-+|-+$/g, '');
                        
                        const districtSlug = venue.districtName?.toLowerCase()
                          .replace(/ı/g, 'i')
                          .replace(/ğ/g, 'g')
                          .replace(/ü/g, 'u')
                          .replace(/ş/g, 's')
                          .replace(/ö/g, 'o')
                          .replace(/ç/g, 'c')
                          .replace(/İ/g, 'i')
                          .replace(/[^a-z0-9]+/g, '-')
                          .replace(/^-+|-+$/g, '');

                        const venueUrl = `/${citySlug}/${districtSlug}/${venue.slug}`;

                        return (
                          <Link
                            key={venue._id}
                            to={venueUrl}
                            onClick={() => setMobileMenuOpen(false)}
                            className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <div className="w-10 h-10 flex-shrink-0 rounded bg-gray-200 overflow-hidden">
                              {venue.images && (
                                <img src={venue.images} alt={venue.name} className="w-full h-full object-cover" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{venue.name}</p>
                              <p className="text-xs text-gray-500 truncate">{venue.districtName}</p>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                <div className="px-4 pt-4 border-t border-gray-200">
                  <Link 
                    to="/giris"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-gradient-to-r from-pink-600 to-pink-700 text-white rounded-lg hover:shadow-lg transition-all"
                  >
                    <Building2 className="w-5 h-5" />
                    <span className="font-medium">İşletme Girişi</span>
                  </Link>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      <OwnerBanner />

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-pink-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
            {/* Brand */}
            <div className="lg:col-span-1">
              <div className="mb-6">
                <img 
                  src="/assets/logo/logo-white.svg" 
                  alt="DavetEviBul Logo" 
                  className="h-14 w-auto"
                />
              </div>
              <p className="text-gray-300 text-sm leading-relaxed mb-6">
                {settings?.footerAbout || "Türkiye'nin en kapsamlı davet evi platformu. Hayallerinizdeki organizasyon için mükemmel mekanı bulun."}
              </p>
              
              {/* Social Media Links */}
              {(settings?.facebookUrl || settings?.instagramUrl || settings?.twitterUrl || settings?.linkedinUrl) && (
                <div className="flex gap-3">
                  {settings?.facebookUrl && (
                    <a 
                      href={settings.facebookUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center hover:bg-pink-600 hover:scale-110 transition-all duration-300"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                  )}
                  {settings?.instagramUrl && (
                    <a 
                      href={settings.instagramUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-600 hover:scale-110 transition-all duration-300"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                  )}
                  {settings?.twitterUrl && (
                    <a 
                      href={settings.twitterUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center hover:bg-blue-500 hover:scale-110 transition-all duration-300"
                    >
                      <Twitter className="w-5 h-5" />
                    </a>
                  )}
                  {settings?.linkedinUrl && (
                    <a 
                      href={settings.linkedinUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center hover:bg-blue-700 hover:scale-110 transition-all duration-300"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-bold text-lg mb-6 text-white relative inline-block">
                Hızlı Erişim
                <span className="absolute -bottom-2 left-0 w-12 h-1 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full"></span>
              </h3>
              <ul className="space-y-3 text-sm">
                <li>
                  <Link to="/" className="text-gray-300 hover:text-pink-400 transition-colors flex items-center gap-2 group">
                    <span className="w-1.5 h-1.5 bg-pink-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                    Ana Sayfa
                  </Link>
                </li>
                <li>
                  <Link to="/davet-evleri" className="text-gray-300 hover:text-pink-400 transition-colors flex items-center gap-2 group">
                    <span className="w-1.5 h-1.5 bg-pink-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                    Tüm Mekanlar
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-gray-300 hover:text-pink-400 transition-colors flex items-center gap-2 group">
                    <span className="w-1.5 h-1.5 bg-pink-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                    Kullanım Koşulları
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-gray-300 hover:text-pink-400 transition-colors flex items-center gap-2 group">
                    <span className="w-1.5 h-1.5 bg-pink-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                    Gizlilik Politikası
                  </Link>
                </li>
                <li>
                  <Link to="/kvkk" className="text-gray-300 hover:text-pink-400 transition-colors flex items-center gap-2 group">
                    <span className="w-1.5 h-1.5 bg-pink-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                    KVKK
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="font-bold text-lg mb-6 text-white relative inline-block">
                İletişim
                <span className="absolute -bottom-2 left-0 w-12 h-1 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full"></span>
              </h3>
              <ul className="space-y-4 text-sm">
                <li>
                  <a 
                    href="https://wa.me/908503077197?text=Merhaba,%20bilgi%20almak%20istiyorum."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-300 hover:text-green-400 transition-colors group cursor-pointer"
                  >
                    <div className="w-10 h-10 bg-green-600/20 rounded-lg flex items-center justify-center group-hover:bg-green-600 transition-colors">
                      <MessageCircle className="w-5 h-5 text-green-400 group-hover:text-white" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs text-green-400">WhatsApp</span>
                      <span>0850 307 71 97</span>
                    </div>
                  </a>
                </li>
                <li>
                  <a 
                    href={`mailto:${settings?.contactEmail || 'info@davetevibul.com'}`}
                    className="flex items-center gap-3 text-gray-300 hover:text-pink-400 transition-colors group cursor-pointer"
                  >
                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center group-hover:bg-pink-600 transition-colors">
                      <Mail className="w-5 h-5" />
                    </div>
                    <span>{settings?.contactEmail || 'info@davetevibul.com'}</span>
                  </a>
                </li>
                <li className="flex items-start gap-3 text-gray-300 hover:text-pink-400 transition-colors group">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center group-hover:bg-pink-600 transition-colors flex-shrink-0">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <span>{settings?.contactAddress || 'İstanbul, Türkiye'}</span>
                </li>
              </ul>
            </div>

            {/* Newsletter/Contact */}
            <div>
              <h3 className="font-bold text-lg mb-6 text-white relative inline-block">
                Bize Ulaşın
                <span className="absolute -bottom-2 left-0 w-12 h-1 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full"></span>
              </h3>
              <p className="text-gray-300 text-sm mb-6 leading-relaxed">
                Sorularınız, önerileriniz veya iş birliği teklifleriniz için bizimle iletişime geçin.
              </p>
              <Button 
                onClick={() => setShowContactDialog(true)}
                className="w-full bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white font-semibold py-6 rounded-xl shadow-lg hover:shadow-pink-500/50 transition-all duration-300 hover:scale-105"
              >
                <Mail className="w-5 h-5 mr-2" />
                İletişime Geç
              </Button>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/10 mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm text-center md:text-left">
                {settings?.footerCopyright || '© 2025 DavetEviBul. Tüm hakları saklıdır.'}
              </p>
              <div className="flex items-center gap-4 text-sm text-gray-400">
                <Link to="/terms" className="hover:text-pink-400 transition-colors">Kullanım Koşulları</Link>
                <span className="text-gray-600">•</span>
                <Link to="/privacy" className="hover:text-pink-400 transition-colors">Gizlilik</Link>
                <span className="text-gray-600">•</span>
                <Link to="/kvkk" className="hover:text-pink-400 transition-colors">KVKK</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Live chat widget — site-wide floating button (bottom-right) */}
      <LiveChatWidget />

      {/* Contact Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>İletişim Formu</DialogTitle>
            <DialogDescription>
              Sorularınız, önerileriniz veya sorun bildirimleri için formu doldurun.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleContactSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Adınız Soyadınız *</Label>
              <Input
                id="name"
                name="name"
                value={contactForm.name}
                onChange={handleContactInputChange}
                placeholder="Ahmet Yılmaz"
                required
              />
            </div>

            <div>
              <Label htmlFor="email">E-posta *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={contactForm.email}
                onChange={handleContactInputChange}
                placeholder="ornek@email.com"
                required
              />
            </div>

            <div>
              <Label htmlFor="phone">Telefon</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={contactForm.phone}
                onChange={handleContactInputChange}
                placeholder="+90 555 123 45 67"
              />
            </div>

            <div>
              <Label htmlFor="subject">Konu *</Label>
              <select
                id="subject"
                name="subject"
                value={contactForm.subject}
                onChange={handleContactInputChange}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                required
              >
                <option value="">Konu seçin</option>
                <option value="Genel Soru">Genel Soru</option>
                <option value="Teknik Sorun">Teknik Sorun</option>
                <option value="Öneri">Öneri</option>
                <option value="Şikayet">Şikayet</option>
                <option value="İşbirliği">İşbirliği</option>
                <option value="Mekan Silme Talebi">Mekan Silme Talebi</option>
                <option value="Diğer">Diğer</option>
              </select>
            </div>

            <div>
              <Label htmlFor="message">Mesajınız *</Label>
              <Textarea
                id="message"
                name="message"
                value={contactForm.message}
                onChange={handleContactInputChange}
                placeholder="Lütfen mesajınızı detaylı bir şekilde yazın..."
                rows={5}
                required
              />
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowContactDialog(false)}
              >
                İptal
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-pink-600 hover:bg-rose-600"
              >
                {submitting ? 'Gönderiliyor...' : 'Gönder'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
